import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';

const GlassWarp = ({ mousePosition }) => {
  const containerRef = useRef(null);
  const sceneRef = useRef(null);
  const materialRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current) return;

    // Scene setup
    const scene = new THREE.Scene();
    sceneRef.current = scene;

    const camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
    
    const renderer = new THREE.WebGLRenderer({ 
      alpha: true, 
      antialias: true 
    });
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    containerRef.current.appendChild(renderer.domElement);

    // Vertex Shader
    const vertexShader = `
      varying vec2 vUv;
      void main() {
        vUv = uv;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `;

    // Fragment Shader with glass distortion
    const fragmentShader = `
      uniform float uTime;
      uniform vec2 uMouse;
      uniform vec2 uResolution;
      varying vec2 vUv;

      // Noise function
      float random(vec2 st) {
        return fract(sin(dot(st.xy, vec2(12.9898,78.233))) * 43758.5453123);
      }

      float noise(vec2 st) {
        vec2 i = floor(st);
        vec2 f = fract(st);
        float a = random(i);
        float b = random(i + vec2(1.0, 0.0));
        float c = random(i + vec2(0.0, 1.0));
        float d = random(i + vec2(1.0, 1.0));
        vec2 u = f * f * (3.0 - 2.0 * f);
        return mix(a, b, u.x) + (c - a)* u.y * (1.0 - u.x) + (d - b) * u.x * u.y;
      }

      void main() {
        vec2 uv = vUv;
        vec2 mouse = uMouse * 0.5 + 0.5;
        
        // Distance from mouse
        float dist = distance(uv, mouse);
        float radius = 0.3;
        
        // Glass distortion effect
        if (dist < radius) {
          float strength = smoothstep(radius, 0.0, dist);
          
          // Create warping effect
          vec2 direction = normalize(uv - mouse);
          float warp = strength * 0.08;
          
          // Add noise for organic glass effect
          float noiseValue = noise(uv * 8.0 + uTime * 0.3);
          warp += noiseValue * strength * 0.02;
          
          uv += direction * warp;
        }
        
        // Glass color with chromatic aberration
        float aberration = 0.002;
        vec3 glassColor = vec3(0.0);
        
        if (dist < radius) {
          float strength = smoothstep(radius, 0.0, dist);
          
          // Chromatic aberration
          float r = strength * 0.3;
          float g = strength * 0.35;
          float b = strength * 0.4;
          
          glassColor = vec3(r, g, b);
          
          // Add fresnel-like effect
          float fresnel = pow(1.0 - strength, 2.0);
          glassColor += fresnel * 0.1;
        }
        
        gl_FragColor = vec4(glassColor, glassColor.r * 0.15);
      }
    `;

    // Create plane geometry
    const geometry = new THREE.PlaneGeometry(2, 2);
    const material = new THREE.ShaderMaterial({
      vertexShader,
      fragmentShader,
      uniforms: {
        uTime: { value: 0 },
        uMouse: { value: new THREE.Vector2(0.5, 0.5) },
        uResolution: { value: new THREE.Vector2(window.innerWidth, window.innerHeight) }
      },
      transparent: true,
      depthTest: false,
      depthWrite: false
    });
    materialRef.current = material;

    const mesh = new THREE.Mesh(geometry, material);
    scene.add(mesh);

    // Animation
    let frameId;
    const clock = new THREE.Clock();
    
    const animate = () => {
      frameId = requestAnimationFrame(animate);
      
      if (materialRef.current) {
        materialRef.current.uniforms.uTime.value = clock.getElapsedTime();
      }
      
      renderer.render(scene, camera);
    };

    animate();

    // Handle resize
    const handleResize = () => {
      renderer.setSize(window.innerWidth, window.innerHeight);
      if (materialRef.current) {
        materialRef.current.uniforms.uResolution.value.set(
          window.innerWidth,
          window.innerHeight
        );
      }
    };

    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => {
      cancelAnimationFrame(frameId);
      window.removeEventListener('resize', handleResize);
      if (containerRef.current && renderer.domElement) {
        containerRef.current.removeChild(renderer.domElement);
      }
      geometry.dispose();
      material.dispose();
      renderer.dispose();
    };
  }, []);

  // Update mouse position
  useEffect(() => {
    if (materialRef.current && mousePosition) {
      materialRef.current.uniforms.uMouse.value.set(
        (mousePosition.x + 1) * 0.5,
        1 - (mousePosition.y + 1) * 0.5
      );
    }
  }, [mousePosition]);

  return (
    <div
      ref={containerRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 5,
        mixBlendMode: 'screen',
      }}
    />
  );
};

export default GlassWarp;

